import React, { FormEvent, useEffect, useState } from 'react';
import Appbar from 'components/appbar/Appbar';
import { Button } from '@mui/material';
import PatientForm from '../form/PatientForm';
import { usePatientValidation } from '../validation/usePatientValidation';
import PatientTab from '../tab/PatientTab';
import AddressFields from 'components/address/AddressFields';
import { AddressErrors, emptyAddress, validateAddress } from 'helpers/addressValidation';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import history from 'services/history';
import InsideSaving from 'components/loading/InsideSaving';
import PatientObservationForm from '../form/PatientObservationForm';
import PatientContactForm from '../form/PatientContactForm';
import { Patient } from 'types/patient';
import { Address } from 'types/address';
import { format } from 'date-fns';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';

// Campo com erro de validação -> aba onde ele está
function tabOfField(field: string) {
  return field === 'email' ? 'contact' : 'main';
}

const NewPatient: React.FC = () => {
  const [validation, setValidation, validate] = usePatientValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState<Patient>({
    id: 0,
    email: '',
    name: '',
    phone: '',
    rg: '',
    document_number: '',
    profession: '',
    active: 1,
    address: null,
    birth_date: null,
    gender: 'N',
    marital_status: '',
    fixed_phone: '',
    observation: '',
  });
  const [patientAddress, setPatientAddress] = useState<Address>(emptyAddress);
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});

  function handleChange<K extends keyof Patient>(index: K, value: Patient[K]) {
    setPatient(prev => ({ ...prev, [index]: value }));
  }

  function handleSubmit() {
    setSaving(true);
    const data = {
      ...patient,
      birth_date: patient.birth_date ? format(patient.birth_date, 'yyyy-MM-dd HH:mm:ss') : '',
      address: patientAddress.zip_code
        ? {
            zip_code: patientAddress.zip_code,
            street: patientAddress.street,
            neighborhood: patientAddress.neighborhood,
            city: patientAddress.city,
            state: patientAddress.state,
            street_number: patientAddress.street_number,
            complement: patientAddress.complement,
          }
        : null,
    };

    api
      .post('/patients', data)
      .then(response => {
        toast.success('Paciente cadastrado');
        // abre o paciente recém-cadastrado, na ficha
        history.push(response.data?.id ? `/patients/${response.data.id}` : '/patients');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível cadastrar o paciente');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    validate(patient)
      .then(() => {
        const errors = validateAddress(patientAddress);
        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
          setTab('address');
          toast.error('Corrija o endereço para salvar');
          return;
        }

        handleSubmit();
        setValidation({});
      })
      .catch(() => {
        // o erro já foi guardado em `validation`
      });
  }

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    const [field] = Object.keys(validation);
    if (field) setTab(tabOfField(field));
  }, [validation]);

  function handleAddressChange(address: Address) {
    setPatientAddress(address);
    setAddressErrors({});
  }

  return (
    <>
      <Appbar title="Pacientes" backAction={() => history.push('/patients')} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title="Novo paciente" backAction={() => history.push('/patients')} />

        <WorkArea>
          <PatientTab onChange={tab => setTab(tab)} tab={tab} />
          <ContentPad>
            <StyledForm onSubmit={handleValidation}>
              {tab === 'address' ? (
                <AddressFields address={patientAddress} onChange={handleAddressChange} errors={addressErrors} />
              ) : tab === 'observation' ? (
                <PatientObservationForm patient={patient} handleChange={handleChange} />
              ) : tab === 'contact' ? (
                <PatientContactForm patient={patient} handleChange={handleChange} validation={validation} />
              ) : (
                <PatientForm handleChange={handleChange} patient={patient} validation={validation} />
              )}
              <button type="submit" style={{ display: 'none' }}></button>
            </StyledForm>
          </ContentPad>
        </WorkArea>

        <RecordFooter>
          <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
            Salvar paciente
          </Button>
        </RecordFooter>
      </PageShell>
    </>
  );
};

export default NewPatient;
